import { FunctionComponent, useEffect, useState } from 'react';
import cn from 'classnames';
import {
  useForm,
  useFieldArray,
  FormProvider,
  SubmitHandler,
} from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Button,
  Section,
  SectionHeading,
  TextField,
  NumberField,
  DateField,
  SelectField,
  TextAreaField,
  Address,
  TrashIcon,
} from '../../components';
import {
  CharLimit100Pattern,
  HiIFormValues,
  Person,
  SsnPattern,
  StateSetupProps,
} from '../../types';
import { cleanIntakeDates, validateDateProvided } from '../../utils/intake';
import { successCallbackPath } from '../../utils/paths';
import { fetchApi } from '../../utils/fetchApi';
import { ApiStatus, IApiData } from '../../utils/types';
import { ManagedReqsByState } from './GenericIntake';
import { useScrollIntoView } from '../../hooks';
import { FormError } from '../../components/forms/FormError';

const EMPLOYEE_DEFAULT_VALUE = { first_name: null, last_name: null, ssn: null };

interface HiIntakeProps extends StateSetupProps {
  isFullWidth?: boolean;
  scrollOnMount?: boolean;
}

export const HiIntake: FunctionComponent<HiIntakeProps> = ({
  peopleData,
  onSubmitSuccess,
  onEnterLater,
  isFullWidth,
  scrollOnMount,
}) => {
  const [scrollIntoViewRef] = useScrollIntoView(scrollOnMount);
  const [searchParams] = useSearchParams();
  const callbackUrl = searchParams.get('callback_url');

  const [hasSubmitError, setHasSubmitError] = useState<boolean>(false);

  const formMethods = useForm<HiIFormValues>();
  const {
    watch,
    handleSubmit,
    control,
    formState: { errors },
  } = formMethods;

  const {
    fields: employeeFields,
    append: appendEmployees,
    remove: removeEmployee,
  } = useFieldArray({
    control,
    name: 'employees',
  });
  const watchEmployees = watch('employees', []);

  const navigate = useNavigate();
  useEffect(() => {
    appendEmployees(EMPLOYEE_DEFAULT_VALUE, { shouldFocus: false });
  }, []);

  // parse people and return those values in the
  // form of options for a select field
  const parseOfficersForSelect = (rawPeople: Person[]) => {
    const result: { name: string; value: string }[] = [{ name: '', value: '' }];
    if (rawPeople) {
      rawPeople.forEach((p) => {
        if (p.is_officer) {
          result.push({
            name: `${p.first_name} ${p.last_name}`,
            value: p.email,
          });
        }
      });
    }
    return result;
  };
  const [officers] = useState<{ name: string; value: string }[]>(
    parseOfficersForSelect(peopleData)
  );

  const handleSubmitSuccess = () =>
    onSubmitSuccess ? onSubmitSuccess() : navigate(`/state-setup/hi/success`);

  const onSubmit: SubmitHandler<HiIFormValues> = (data) => {
    setHasSubmitError(false);
    const cleanedData = cleanIntakeDates(data);
    const payload = { data: cleanedData };

    fetchApi({
      url: `/api/form_captures/hi`,
      method: 'POST',
      body: payload,
    }).then(({ status }: IApiData) => {
      if (status === ApiStatus.Error) {
        setHasSubmitError(true);
      } else if (ManagedReqsByState.hi) {
        const reqIds: string[] = ManagedReqsByState.hi;
        // Set the requirement to managed and in-progress
        fetchApi({
          url: `/api/requirements/batch`,
          method: 'PATCH',
          body: {
            patch: { operation: 'mark-as-managed' },
            data_ids: reqIds,
          },
        }).then(({ status: status2 }: IApiData) => {
          if (status2 === ApiStatus.Error) {
            setHasSubmitError(true);
          } else {
            // Create and start workflow for requirements
            fetchApi({
              url: `/api/workflows`,
              method: 'POST',
              body: {
                data_ids: reqIds,
                region_code: 'hi',
              },
            }).then(({ status: status3 }: IApiData) => {
              if (status3 === ApiStatus.Error) {
                setHasSubmitError(true);
              } else if (callbackUrl) {
                navigate(successCallbackPath(callbackUrl), { replace: true });
              } else {
                handleSubmitSuccess();
              }
            });
          }
        });
      } else {
        handleSubmitSuccess();
      }
    });
  };

  return (
    <Section className="flex flex-col items-center" ref={scrollIntoViewRef}>
      <SectionHeading
        className={cn('pb-4', {
          'w-full': isFullWidth,
          'w-96': !isFullWidth,
        })}
        text="Hawaii Employer Setup"
      />
      <FormProvider {...formMethods}>
        <form
          className={cn('my-8 ', {
            'w-full': isFullWidth,
            'w-96': !isFullWidth,
          })}
          onSubmit={handleSubmit(onSubmit)}
        >
          <TextField
            name="dba_name"
            label="Alternative Business Name (optional)"
            placeholder="Acme's Trade Name"
            description="Trade name or DBA if different from the company's legal business name."
            error={errors.dba_name}
          />
          <TextAreaField
            name="business_activities_description"
            label="Description of Business Activities"
            placeholder="Acme Inc. provides compliance software to businesses hiring remotely."
            description="Provide a description of what products and/or services your business provides in Hawaii. This information is used by the Hawaii Department of Taxation to classify your business."
            error={errors.business_activities_description}
            reactFormConfig={{
              required: 'This is required',
              pattern: CharLimit100Pattern,
            }}
          />
          <DateField
            name="activity_start_date"
            label="First Hawaii Employee Start Date"
            description="The Hawaii Department of Labor and Industrial Relations requires that employees must have started prior to opening an Unemployment Insurance account. If the start date is in the future, registration won't occur until after the start date."
            error={errors.activity_start_date}
            reactFormConfig={{
              required: 'This is required',
              validate: {
                isDate: validateDateProvided,
              },
            }}
          />
          <NumberField
            name="num_hi_locations"
            label="Number of Hawaii Locations"
            description="If you only have employees working from home enter 0."
            min="0"
            error={errors.num_hi_locations}
            reactFormConfig={{
              required: 'This is required',
            }}
          />
          <div className="mb-8">
            <div>
              <label className="mb-2 block font-semibold">Hawaii Address</label>
              <p className="mb-4 text-sm text-gray-600">
                Hawaii requires a local physical address. If there are no
                physical locations, Hawaii will accept a local employee&apos;s
                home address. The address cannot be a P.O. Box.
              </p>
              <Address
                error={errors.physical_address}
                name="physical_address"
                defaultState="Hawaii"
              />
            </div>
          </div>
          <div className="mb-8">
            <div>
              <div>
                <h2 className="block pb-2 text-xl font-semibold text-gray-700">
                  Your New Hire
                </h2>
                <p className="mb-4 text-sm text-gray-600">
                  Please add your new hires in Hawaii.
                </p>
                <ul>
                  {employeeFields.map((item: any, index) => {
                    return (
                      <li
                        key={item.id}
                        className="mb-4 rounded border bg-gray-100 p-4"
                      >
                        {/* HACK: This is suuuper awkward, but `register`
                        expects a string literal and typescript can't
                        figure it out without casting. */}
                        <TextField
                          name={`employees.${index}.first_name`}
                          label="First name"
                          error={errors.employees?.[index]?.first_name}
                          reactFormConfig={{
                            required: 'This is required',
                          }}
                        />
                        <TextField
                          name={`employees.${index}.last_name`}
                          label="Last name"
                          error={errors.employees?.[index]?.last_name}
                          reactFormConfig={{
                            required: 'This is required',
                          }}
                        />
                        <TextField
                          name={`employees.${index}.ssn`}
                          label="SSN"
                          error={errors.employees?.[index]?.ssn}
                          reactFormConfig={{
                            required: 'This is required',
                            pattern: SsnPattern,
                          }}
                        />
                        <div className="flex flex-row-reverse">
                          <Button
                            type="button"
                            variant="secondary"
                            leftIcon={<TrashIcon className="size-4" />}
                            onClick={() => removeEmployee(index)}
                          >
                            Delete
                          </Button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
            <Button
              type="button"
              variant="secondary"
              onClick={() => appendEmployees(EMPLOYEE_DEFAULT_VALUE)}
            >
              Add another new hire
            </Button>
            {watchEmployees.length === 0 && (
              <FormError
                errorMessage="You must add at least 1 new hire"
                margin="mt-1"
              />
            )}
          </div>
          <SelectField
            name="representative.ref_email"
            label="Company Representative"
            description="Hawaii requires that you assign a company representative."
            options={officers}
            error={errors.representative?.ref_email}
            reactFormConfig={{
              required: 'This is required',
            }}
          />
          <SelectField
            name="contact.ref_email"
            label="Contact"
            description="Hawaii also requires that you assign a point of contact at your company."
            options={officers}
            error={errors.contact?.ref_email}
            reactFormConfig={{
              required: 'This is required',
            }}
          />
          <div className="mt-4 flex">
            <div className="flex gap-x-4">
              {onEnterLater && (
                <Button
                  type="button"
                  variant="grayTertiary"
                  size="large"
                  isFullWidth
                  onClick={onEnterLater}
                >
                  Enter later
                </Button>
              )}
              <Button type="submit" isFullWidth size="large">
                Submit
              </Button>
            </div>
            {hasSubmitError && <FormError />}
          </div>
        </form>
      </FormProvider>
    </Section>
  );
};

export default HiIntake;
